import { shallowRef, ref, inject } from 'vue'
import type { Currency } from '@gat-ship/types'
import {
  appLoadingKey,
  currenciesKey,
  type CurrenciesService
} from '@gat-ship/types/injection-keys'
import { useCurrencyApiCalls } from './useCurrencyApiCalls'
import { useCurrencySignalREvents } from '@gat-ship/signalr'
import { globalUtils } from '@gat-ship/utils'

function useCurrenciesState() {
  const loading = inject(appLoadingKey)!

  const currencies = shallowRef<Currency[]>([])

  function setCurrencies(data: Currency[]) {
    currencies.value = data.sort((a, b) => a.code.localeCompare(b.code))
  }

  const {
    getLibraryCurrenciesRequest,
    addCurrencyRequest,
    removeCurrencyRequest
  } = useCurrencyApiCalls()

  const isFetched = ref(false)

  async function fetchCurrencies(forceFetch?: boolean) {
    if (isFetched.value && !forceFetch) {
      return
    }
    loading.setLoadingId('currencies')
    const result = await getLibraryCurrenciesRequest()
    setCurrencies(result.currencies)
    loading.removeLoadingId('currencies')
    isFetched.value = true
  }

  async function addCurrency(currency: Currency) {
    const commandId = globalUtils.functions.uuid()
    loading.setLoadingId('currency-' + currency.code)
    await addCurrencyRequest({
      commandId,
      payload: { code: currency.code }
    })
    const result = [...currencies.value, currency]
    setCurrencies(result)
    loading.removeLoadingId('currency-' + currency.code)
  }

  function removeCurrencyLocal(code: string) {
    const result = currencies.value.filter((item) => item.code !== code)
    setCurrencies(result)
  }

  async function removeCurrency(code: string) {
    const commandId = globalUtils.functions.uuid()
    loading.setLoadingId('currency-' + code)
    await removeCurrencyRequest({
      commandId,
      code: code
    })
    removeCurrencyLocal(code)
    loading.removeLoadingId('currency-' + code)
  }
  return {
    /** The list of currencies. */
    currencies,
    /** Add a currency to the library. */
    add: addCurrency,
    /** Remove a currency from the library. */
    remove: removeCurrency,
    /** Remove a currency from local state only (no API call). */
    removeLocal: removeCurrencyLocal,
    /** Fetch the currencies from the library. */
    fetch: fetchCurrencies
  }
}

function useAllPlatformCurrenciesState() {
  const allPlatformCurrencies = shallowRef<Currency[]>([])

  function setAllPlatformCurrencies(data: Currency[]) {
    allPlatformCurrencies.value = data
  }

  const { getAllPlatformCurrenciesRequest } = useCurrencyApiCalls()

  const isFetched = ref(false)

  async function fetchAllPlatformCurrencies(forceFetch?: boolean) {
    if (isFetched.value && !forceFetch) {
      return
    }
    const result = await getAllPlatformCurrenciesRequest()
    setAllPlatformCurrencies(result.currencies)
    isFetched.value = true
  }
  return {
    allPlatformCurrencies,
    fetch: fetchAllPlatformCurrencies
  }
}

/**
 * Data source for the currency library: reactive `currencies` +
 * `allPlatformCurrencies`, optimistic add/remove, and SignalR live-sync.
 *
 * The loading handle is the app-wide loading registry, provided once at the
 * root by `LoadingProvider` and consumed here via its injection key
 * (`appLoadingKey`, keyed in `@gat-ship/types`) rather than threaded in as a
 * parameter. The returned object conforms to {@link CurrenciesService}
 * (verified via `satisfies`) while keeping the concrete `signalR` member —
 * `CurrenciesView` drives the subscription externally and the runtime-free
 * contract omits it.
 */
export function useCurrencies() {
  const currenciesState = useCurrenciesState()

  const allPlatformCurrenciesState = useAllPlatformCurrenciesState()

  const signalR = useCurrencySignalREvents({
    onCurrencyAdded: () => {
      currenciesState.fetch(true)
    },
    onCurrencyRemoved: (payload) => {
      currenciesState.removeLocal(payload.message.code)
    }
  })

  const service = {
    currencies: currenciesState.currencies,
    allPlatformCurrencies: allPlatformCurrenciesState.allPlatformCurrencies,
    addCurrency: currenciesState.add,
    removeCurrency: currenciesState.remove,
    fetchCurrencies: currenciesState.fetch,
    fetchAllPlatformCurrencies: allPlatformCurrenciesState.fetch,
    signalR
  }
  return service satisfies CurrenciesService
}

/** Inject the currencies context provided by a parent provider. */
export function useCurrenciesContext() {
  return inject(currenciesKey, undefined)
}
